/**
 * URL duration keywords (1-day, 10-day, 30-day, 1-year).
 * Map: keyword -> { vehicle_type, selected_duration } for pre-selecting the vignette duration card.
 */
export const DURATION_KEYWORDS = {
    '1-day':   { vehicle_type: '1-day', selected_duration: 1 },
    '10-day':  { vehicle_type: '10-day', selected_duration: 10 },
    '30-day':  { vehicle_type: '30-day', selected_duration: 30 },
    '1-year':  { vehicle_type: '1-year', selected_duration: 365 },
};

const VALID_KEYWORDS = Object.keys(DURATION_KEYWORDS);

/** Backend description variants -> canonical key (1-day, 10-day, 30-day, 1-year). */
const DESCRIPTION_TO_KEY = {
    '1-day': '1-day',
    '10-day': '10-day',
    '30-day': '30-day',
    '1-year': '1-year',
    'vignette-1-day': '1-day',
    'vignette-10-days': '10-day',
    'vignette-30-days': '30-day',
    'vignette-365-days': '1-year'
};

/**
 * Normalize backend page description to a duration keyword. Use when URL slug can vary (e.g. 30-daydddddd) but backend sends description = "30-day".
 * @param {string} description - Backend field description
 * @returns {string|null} - One of '1-day'|'10-day'|'30-day'|'1-year' or null
 */
export function normalizeDurationKey(description) {
    if (!description || typeof description !== 'string') return null;
    const trimmed = description.trim().toLowerCase();
    if (VALID_KEYWORDS.includes(trimmed)) return trimmed;
    const mapped = DESCRIPTION_TO_KEY[trimmed] || DESCRIPTION_TO_KEY[description];
    if (mapped) return mapped;
    if (/^vignette[-_]1[-_]?day$/i.test(trimmed)) return '1-day';
    if (/^vignette[-_]10[-_]?days?$/i.test(trimmed)) return '10-day';
    if (/^vignette[-_]30[-_]?days?$/i.test(trimmed)) return '30-day';
    if (/^vignette[-_](365|1[-_]?year)$/i.test(trimmed)) return '1-year';
    if (/^1[-_]?year$/i.test(trimmed)) return '1-year';
    if (/^1[-_]?day$/i.test(trimmed)) return '1-day';
    if (/^10[-_]?day/i.test(trimmed)) return '10-day';
    if (/^30[-_]?day/i.test(trimmed)) return '30-day';
    return null;
}

/**
 * Sort rank for duration cards: 1, 10, 30, 365. Unknown slugs sort last.
 * @param {string} description - Same shape as getCategoryDescription output
 * @returns {number}
 */
export function getDurationSortRank(description) {
    const key = normalizeDurationKey(description);
    if (!key || !DURATION_KEYWORDS[key]) return Number.MAX_SAFE_INTEGER;
    return DURATION_KEYWORDS[key].selected_duration;
}

function firstStringValue(value) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (!value || typeof value !== 'object') return '';
    for (const v of Object.values(value)) {
        if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
}

/** Read key from product.category.description (or translated fallback). */
export function getCategoryDescription(product) {
    if (!product || typeof product !== 'object') return '';
    const category = product.category || {};
    const translatedDescription = firstStringValue(category.translated?.description);
    if (translatedDescription) return translatedDescription;
    const description = firstStringValue(category.description);
    if (description) return description;
    return '';
}

/** Locale-stable category id from API (for matching formData.category_id). */
export function getCategoryId(product) {
    if (!product || typeof product !== 'object') return null;
    const category = product.category || {};
    const raw = category.id ?? category.category_id ?? product.category_id;
    if (raw == null || raw === '') return null;
    return raw;
}

/**
 * Canonical duration slug: prefer non-translated category.description so it matches URL presets
 * (e.g. 30-day) even when translated.description is localized.
 */
export function getCategoryCanonicalDescription(product) {
    if (!product || typeof product !== 'object') return '';
    const category = product.category || {};
    const base = firstStringValue(category.description);
    if (base) return base;
    return firstStringValue(category.translated?.description) || '';
}

/** Map category.key (vehicle_type) to backend object key for duration paths. */
export const CATEGORY_KEY_TO_BACKEND = {
    '1-day': '1-day',
    '10-day': '10-day',
    '30-day': '30-day',
    '1-year': '1-year',
    'vignette-1-day': '1-day',
    'vignette-10-days': '10-day',
    'vignette-30-days': '30-day',
    'vignette-365-days': '1-year',
};

export function getDurationFromPath(pathname) {
    if (!pathname || typeof pathname !== 'string') return null;
    const normalized = pathname.replace(/^\/+|\/+$/g, '');
    const match = normalized.match(/buy-vignette\/([^/]+)/);
    let segment = match ? match[1].toLowerCase().replace(/^\/+|\/+$/g, '') : null;
    if (!segment && normalized) {
        const lastSegment = normalized.split('/').pop()?.toLowerCase();
        if (lastSegment) segment = lastSegment;
    }
    return segment ? normalizeDurationKey(segment) : null;
}
